import type { HotkeysProvider, Hotkey } from 'angular-hotkeys';
import type { IDocumentService, IScope, IAugmentedJQuery } from 'angular';
import { EventService } from './event-service';
import { EventCodes } from '../model/event-codes';
import { I18n } from '../../i18n/mx-internationalization';

export interface HotKeyOptions {
	key: string | string[];
	description?: string;
	callback: Hotkey['callback'];
	scope?: IScope;
	onRegister?: () => void;
}
/**
 * @ngdoc service
 * @name mx.shell.KeyboardNavigationService
 * @module mx.shell
 *
 * @description Provides registring/unregistring hotkeys functionality
 */
export class KeyboardNavigationService {
	private registeredKeys: Record<string, HotKeyOptions> = {};
	private formatterApplied = false;
	private isMac = window.navigator.platform.toUpperCase().includes('MAC');
	private cheatSheetEl?: IAugmentedJQuery;

	static readonly $inject = ['$document', 'hotkeys', 'mx.shell.EventService', 'mx.internationalization'];
	constructor(
		private readonly $document: IDocumentService,
		private readonly hotkeys: HotkeysProvider,
		private readonly eventService: EventService,
		private readonly i18n: I18n
	) {
		this.registerAdditionalOpenKeyForARIA();
		this.watchCheatSheetOpened(this.applyARIA);
	}

	public toggleCheatSheet() {
		this.hotkeys.toggleCheatSheet();
	}

	private isCheatSheetOpened() {
		this.cheatSheetEl = this.cheatSheetEl || this.$document.find<HTMLElement>('.cfp-hotkeys-container');
		return this.cheatSheetEl?.[0].classList.contains('in') || false;
	}

	public getKeyOptions(key: string) {
		return this.registeredKeys[key];
	}

	public registerKey(options: HotKeyOptions | HotKeyOptions[]) {
		const isMultipleKeys = Array.isArray(options);

		if (isMultipleKeys) {
			this.validateOptions(options);
			this.applyKeys(options);
		} else {
			this.validateOption(options);
			this.applyKey(options);
		}
	}

	private applyKeys(options: HotKeyOptions[]) {
		options.forEach((option) => {
			this.applyKey(option);
		});
	}

	private applyKey(options: HotKeyOptions) {
		const key = options.key;
		const description = options.description || 'No description';
		const callback = options.callback;
		const scope = options.scope;
		const onRegister = options.onRegister;

		if (typeof key === 'string') {
			this.registeredKeys[key] = options;
		} else if (Array.isArray(key)) {
			key.forEach((k) => {
				this.registeredKeys[k] = options;
			});
		}

		if (onRegister) {
			onRegister();
		}

		const callbackWrapper: Hotkey['callback'] = (...params) => {
			if (this.isCheatSheetOpened()) {
				this.toggleCheatSheet();
			}
			this.eventService.dispatchEvent(EventCodes.TrackTelemetry, {
				eventName: 'Hotkeys pressed',
				sender: params[1]?.combo?.[0],
				senderTitle: params[1]?.description,
			});
			callback(...params);
		};

		const hotkey: Hotkey = {
			combo: key,
			description,
			callback: callbackWrapper,
			allowIn: ['INPUT', 'SELECT', 'TEXTAREA'],
		};

		if (scope) {
			/* legacy code. Kept here as it can be reused in a future by Nick
			if (nameInTemplate) {
				if (hint) {
					scope[nameInTemplate] = hint;
				} else {
					scope[nameInTemplate] = key.split('+').slice(1).join('+');
				}
			}*/

			scope.$on('$destroy', () => {
				if (typeof key === 'string') {
					delete this.registeredKeys[key];
				} else if (Array.isArray(key)) {
					key.forEach((k) => {
						delete this.registeredKeys[k];
					});
				}
			});

			this.hotkeys.bindTo(scope).add(hotkey);
		} else {
			const hotkeyInstance = this.hotkeys.add(hotkey);
			if (!this.formatterApplied) {
				this.rewriteHotKeyFormatter(hotkeyInstance);
				this.formatterApplied = true;
			}
		}
	}

	private rewriteHotKeyFormatter(hotKey: Hotkey) {
		//dirty hack is used here as 'angular-hotkeys' library doesn't normally support keys formatting for macOS
		// HotKey.format is rewritten with our formatter which is based in original one from 'angular-hotkeys'

		// eslint-disable-next-line @typescript-eslint/no-this-alias
		const service = this;
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		hotKey.__proto__.format = function () {
			this._formated = this._formated || this.combo.map((key: string) => service.symbolize(key));
			return this._formated;
		};
	}

	private symbolize(combo: string) {
		const map = {
			command: '\u2318', // ⌘
			option: '\u2325', // ⌥
			left: '\u2190', // ←
			right: '\u2192', // →
			up: '\u2191', // ↑
			down: '\u2193', // ↓
			return: '\u23CE', // ⏎
			backspace: '\u232B', // ⌫
		};
		return combo
			.split('+')
			.map((originalKey) => {
				let key = originalKey;
				if (this.isMac) {
					if (key === 'ctrl') {
						key = 'command';
					} else if (key === 'alt') {
						key = 'option';
					}
				}

				return map[key as keyof typeof map] || key;
			})
			.join(' + ');
	}

	public unregisterKey(key: string) {
		if (!key) {
			throw new Error('No hotkey to remove provided.');
		}

		this.hotkeys.del(key);
		delete this.registeredKeys[key];
	}

	public listKeys() {
		return Object.keys(this.registeredKeys);
	}

	private validateOptions(options: HotKeyOptions[]) {
		options.forEach((option) => {
			this.validateOption(option);
		});
	}

	private validateOption(option: HotKeyOptions) {
		if (!option) {
			throw new Error('No options object provided.');
		}

		if (!option.key) {
			throw new Error('No hotkey to add provided.');
		}

		if (!option.callback) {
			throw new Error('No callback for a hotkey provided.');
		}
	}

	private watchCheatSheetOpened(callback: () => void) {
		const cheatSheetOpenedObserver = new MutationObserver((mutations) => {
			mutations.forEach((mutation) => {
				if (mutation.type === 'attributes' && mutation.attributeName === 'class' && this.isCheatSheetOpened()) {
					callback.apply(this);
				}
			});
		});
		cheatSheetOpenedObserver.observe($('.cfp-hotkeys-container')[0], { attributes: true });
	}

	private registerAdditionalOpenKeyForARIA() {
		this.registerKey({
			key: 'alt+h',
			description: '$$undefined$$',
			callback: (e) => {
				if (!this.isCheatSheetOpened()) {
					this.toggleCheatSheet();
				}
				e.preventDefault();
			},
		});
	}

	private applyARIA() {
		$('.cfp-hotkeys').attr('aria-description', this.i18n.get('components.common.ARIA-escToClose'));
		$('#mx-cfp-hotkeys-title').text(this.i18n.get('shell.mx-shell-toolbar.showKeyboardShortcuts'));
		const closeLabel = this.i18n.get('shell.mx-shell-toolbar.ARIA-closeKeyboardShortcutsHelp') + ' ' + this.i18n.get('components.common.ARIA-escToClose');
		$('.cfp-hotkeys-close').attr('aria-label', closeLabel).attr('title', closeLabel);

		if ($('.cfp-hotkeys-key:first').text() === '?') {
			$('.cfp-hotkeys-key:first').text(this.symbolize('alt+h'));
			$('.cfp-hotkeys-text:first').text(this.i18n.get('shell.hotkeys.showHideShortcuts'));
		}

		setTimeout(() => {
			$('#mx-cfp-hotkeys-title').trigger('focus');
		}, 500);
	}
}

export const KeyboardNavigationTeplate = `
<div class="cfp-hotkeys-container fade" ng-class="{in: helpVisible}" style="display: none;">
	<div class="mx-aria-only" tabindex="0" onfocus="$('.cfp-hotkeys-close')[0]?.focus()"></div>
	<div
		class="cfp-hotkeys"
		aria-modal="true"
		role="dialog"
		aria-labelledby="mx-cfp-hotkeys-title"
		tabindex="-1">
		<h2
			id="mx-cfp-hotkeys-title"
			class="cfp-hotkeys-title"
			tabindex="0"
			ng-if=":: !header">
			{{:: title }}
		</h2>
		<div ng-bind-html=":: header" ng-if=":: header"></div>
		<ul id="mx-cfp-hotkeys-list" aria-labeledby="mx-cfp-hotkeys-title">
			<li ng-repeat="hotkey in hotkeys | filter:{ description: '!$$undefined$$' } track by hotkey.key+hotkey.description">
				<span class="cfp-hotkeys-keys">
					<kbd ng-repeat="key in hotkey.format() track by key" class="cfp-hotkeys-key">{{:: key }}</kbd>
				</span>
				<span class="cfp-hotkeys-text">{{:: hotkey.description }}</span>
			</li>
		</ul>
		<div ng-bind-html=":: footer" ng-if=":: footer"></div>
		<div
			class="cfp-hotkeys-close"
			ng-click="toggleCheatSheet()"
			role="button"
			tabindex="0"
			mx-keyboard-activation="toggleCheatSheet()"
		><span aria-hidden="true" role="presentation">&#215;</span></div>
		<div class="mx-aria-only" tabindex="0" onfocus="$('#mx-cfp-hotkeys-title')[0]?.focus()"></div>
	</div>
</div>`;
